import {
  useCallback,
  useEffect,
  useState,
  forwardRef,
  useImperativeHandle,
  Suspense,
  lazy,
  memo,
  useRef,
  useMemo,
} from "react";
import "dayjs/locale/nb";
import {
  Dialog,
  DialogContent,
  TextField,
  Select,
  MenuItem,
  Autocomplete,
  Avatar,
  Chip,
  Checkbox,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Popover,
} from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs, { Dayjs } from "dayjs";
import duration from "dayjs/plugin/duration";

import {
  X,
  Clock,
  MapPin,
  Users as UsersIcon,
  Video,
  AlertCircle,
  CalendarIcon,
  FileText,
  Loader2,
  Trash2,
  Trash,
  Bell,
  BellOff,
  Phone,
  Lock,
  User,
  Briefcase,
  UserPlus,
  Square,
  SquareCheck,
} from "lucide-react";
import { toast } from "react-toastify";
import { CalendarEventDetails, CalendarEvent } from "@/api/calendarTypes";
import {
  MeetingContact,
  QueryContact,
  QueryPortfolio,
  QueryProspect,
  QueryTask,
} from "@/api/queries/types";
import { useGetSavedQuery } from "@/api/queries/useGetSavedQuery";
import { useGetSavedQuery2 } from "@/api/queries/useGetSavedQuery";
import { useSaveTask } from "@/api/tasks/saveTask";
import { useUpdateMeetingTask } from "@/api/tasks/updateMeetingTask";
import { useAuth } from "@/auth/useAuth";
import { Button } from "@/components/Button/Button";
import { MeetingDescriptionEditor } from "@/components/Calendar/MeetingDescriptionEditor";
import { LogoAnimation } from "@/components/Logo/logoAnimation";
import { MEETING_ROOMS } from "@/components/MeetingRooms/MeetingRoomAvailability";
import { htmlToText } from "@/lib/utils";
import { useKeycloak } from "@/providers/KeycloakProvider";
import {
  createEvent,
  getCalendarEvents,
  getMyProfile,
} from "@/services/graphServices";
import { deleteEvent } from "@/services/graphServices/deleteEvent";
import { updateCalendarEvent } from "@/services/graphServices/updateCalendarEvent";

dayjs.extend(duration);

// Memoized so typing in unrelated fields (title, description) doesn't force
// this heavy weekly-availability grid to re-render.
const ScheduleAssistant = memo(
  lazy(() =>
    import("@/components/Calendar/scheduling-assistant").then((module) => ({
      default: module.ScheduleAssistant,
    }))
  )
);

// Unified attendee structure with type information
interface MeetingAttendee {
  email?: string;
  name?: string;
  type: "Contact" | "Organizer" | "Manual" | "Advisor";
  invite: boolean;
  contact?: MeetingContact | Advisor; // Reference to CRM contact if type is "Contact"
}

export interface NewMeetingRef {
  open: (
    time: Dayjs,
    resource?: string,
    prefilledContactIds?: string[],
    eventToEdit?: CalendarEvent,
    endTime?: Dayjs,
    relatedTask?: QueryTask
  ) => void;
  close: () => void;
}

interface NewMeetingProps {
  onSuccess?: () => void;
}

interface Advisor {
  id: number;
  contactId: string;
  name: string;
  email: string;
}

export const NewMeeting = forwardRef<NewMeetingRef, NewMeetingProps>(
  ({ onSuccess }, ref) => {
    const [loading, setLoading] = useState(false);
    const [open, setOpen] = useState(false);
    const [editingEventId, setEditingEventId] = useState<string | null>(null);
    const [relatedTask, setRelatedTask] = useState<QueryTask | null>(null);
    const [createTask, setCreateTask] = useState(true);
    const [room, setRoom] = useState<string>("");
    const [meetingAttendees, setMeetingAttendees] = useState<MeetingAttendee[]>(
      []
    ); // Unified attendees list
    const [myEmail, setMyEmail] = useState<string>("");
    const [myName, setMyName] = useState<string>("");
    // Set when the Microsoft profile/token can't be fetched (signed out, popup blocked, cooldown).
    const [authError, setAuthError] = useState<string | null>(null);
    const [hasConflict, setHasConflict] = useState(false);
    const [category, setCategory] = useState<string>("Meeting");
    const [repPopoverAnchor, setRepPopoverAnchor] =
      useState<HTMLElement | null>(null);
    const [repPopoverIndex, setRepPopoverIndex] = useState<number | null>(null);
    // "details" vs "availability" â€” the weekly grid only mounts when this
    // tab is active, and there's a single instance instead of one per
    // responsive breakpoint, so typing in the form never re-renders it.
    const [activeTab, setActiveTab] = useState<"details" | "availability">(
      "details"
    );

    const { updateMeetingTaskTime, submitting } = useUpdateMeetingTask();

    const { data: advisorsData, loading: loadingAdvisors } =
      useGetSavedQuery2<QueryPortfolio>(
        "advPortalPortfoliosWithParameters",
        {
          status: ["A"],
          portfolioType: ["Commisssion", "Commission"],
        },
        undefined,
        undefined,
        true
      );

    const [meetingTypeSelected, setMeetingTypeSelected] =
      useState<boolean>(false);
    const [meeting, setMeeting] = useState<CalendarEventDetails>({
      subject: "",
      start: {
        dateTime: dayjs().format("YYYY-MM-DDTHH:mm:ss"),
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      },
      end: {
        dateTime: dayjs().add(30, "minute").format("YYYY-MM-DDTHH:mm:ss"),
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      },
      body: {
        contentType: "html",
        content: "",
      },
      attendees: [],
      isOnlineMeeting: true,
      onlineMeetingProvider: "teamsForBusiness",
      isReminderOn: true,
      reminderMinutesBeforeStart: 15,
      categories: ["Meeting"],
    });

    const { linkedContact, access, userProfile } = useKeycloak();
    const [contacts, setContacts] = useState<MeetingContact[]>([]);
    const [contactsLoading, setContactsLoading] = useState(false);
    const { fetchQueryResults } = useGetSavedQuery();
    const { saveTask } = useSaveTask();

    const advisors = useMemo(() => {
      return (
        advisorsData
          ?.map((x) => ({
            id: x.contact_id,
            contactId: x.contactId,
            name: x.contactName,
            email: x.contactEmail,
          }))
          .filter(
            (x) =>
              x.id !== Number(linkedContact) &&
              !meetingAttendees.some(
                (attendee) =>
                  attendee.type === "Advisor" &&
                  attendee.contact?.contactId === x.contactId
              )
          ) || []
      ).sort((a, b) => a.name.localeCompare(b.name));
    }, [advisorsData, linkedContact, meetingAttendees]);

    useEffect(() => {
      const customParameters = {
        limit: 3000,
        contactName: "%",
      };
      const fetchContacts = async () => {
        setContactsLoading(true);
        try {
          const results = await fetchQueryResults<QueryContact>(
            "advPortalContactsSimple",
            customParameters
          );
          const prospects = await fetchQueryResults<QueryProspect>(
            "advPortalProspects",
            customParameters
          );
          const contactsList =
            results.data?.savedQuery.queryResult.queryResultJson || [];
          const prospectsList =
            prospects.data?.savedQuery.queryResult.queryResultJson || [];

          setContacts(
            [...contactsList, ...prospectsList].sort((a, b) =>
              a.name.localeCompare(b.name)
            )
          );
        } catch (error) {
          console.error("Error fetching contacts:", error);
        } finally {
          setContactsLoading(false);
        }
      };
      fetchContacts();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const { getToken, resetCooldown } = useAuth();

    useEffect(() => {
      const fetchProfile = async () => {
        const profile = await ensureOrganizer();
        if (profile?.mail) {
          setMyEmail(profile.mail);
          setMyName(profile.displayName);

          // Only add organizer if list is completely empty (new meeting without prefill)
          setMeetingAttendees((prev) => {
            if (prev.length === 0) {
              return [
                {
                  email: profile.mail,
                  name: profile.displayName || profile.mail,
                  type: "Organizer",
                  invite: true,
                },
              ];
            }
            return prev;
          });
        }
      };
      if (open) fetchProfile();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const getAllAttendees = useCallback((): string[] => {
      // Get only attendees who should be invited (invite = true)
      const invitedEmails = meetingAttendees
        .filter((a) => a.invite && a.email)
        .map((a) => a.email as string);
      const combined = room ? [...invitedEmails, room] : [...invitedEmails];
      const seen = new Set<string>();
      return combined
        .filter(Boolean)
        .map((e) => e.trim())
        .filter((email) => {
          const key = email.toLowerCase();
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        });
    }, [meetingAttendees, room]);

    const scheduleAttendees = useMemo(() => {
      const initial = myEmail
        ? [myEmail, ...getAllAttendees()]
        : getAllAttendees();
      const seen = new Set<string>();
      return initial
        .filter(Boolean)
        .map((email) => email.trim())
        .filter((email) => {
          const key = email.toLowerCase();
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        });
    }, [getAllAttendees, myEmail]);

    // Stable Date instances so ScheduleAssistant's memoization survives
    // unrelated re-renders (e.g. typing in title/description).
    const scheduleStart = useMemo(
      () => dayjs(meeting?.start?.dateTime).toDate(),
      [meeting?.start?.dateTime]
    );
    const scheduleEnd = useMemo(
      () => dayjs(meeting?.end?.dateTime).toDate(),
      [meeting?.end?.dateTime]
    );

    // Memoized: only depends on attendee/contact state, so typing in the
    // title or description doesn't re-render this heavy table + autocomplete
    // subtree. Must be called unconditionally (not inside the tab branch)
    // to satisfy the rules of hooks.
    const participantsSection = useMemo(
      () => (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <label className="text-sm font-medium flex items-center gap-2">
              <UsersIcon className="size-4" />
              Participants ({meetingAttendees.length})
            </label>
            <div className="h-6 flex items-center">
              <button
                type="button"
                aria-pressed={createTask}
                onClick={() => setCreateTask((v) => !v)}
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
                  createTask
                    ? "bg-blue-100 text-blue-700 hover:bg-blue-200"
                    : "bg-gray-100 text-gray-500 hover:bg-gray-200"
                }`}
              >
                {createTask ? (
                  <SquareCheck
                    className="size-3.5 flex-shrink-0"
                    aria-hidden="true"
                  />
                ) : (
                  <Square
                    className="size-3.5 flex-shrink-0"
                    aria-hidden="true"
                  />
                )}
                <span>
                  {createTask
                    ? "Task will be created"
                    : "No task - click to enable"}
                </span>
              </button>
            </div>
          </div>

          {/* Add Participant Inputs - Side by Side */}
          <div className="grid grid-cols-[repeat(auto-fit,minmax(160px,1fr))] gap-1.5">
            {/* Add Contact via Autocomplete */}
            <Autocomplete
              loading={contactsLoading}
              size="small"
              fullWidth
              options={contacts || []}
              getOptionLabel={(option: MeetingContact) =>
                `${option.name}${option.email ? ` (${option.email})` : ""}`
              }
              renderOption={(props, option) => {
                const isProspect = "representatives" in option;
                const noEmail = !option.email;
                return (
                  <li {...props} key={option.contactId}>
                    <div className="flex items-center justify-between w-full gap-2 min-w-0">
                      <div className="flex flex-col min-w-0">
                        <span className="truncate text-sm">
                          {option.name}
                          {option.email ? ` (${option.email})` : ""}
                        </span>
                        {noEmail && (
                          <span className="text-[10px] text-muted-foreground">
                            No email - task only
                          </span>
                        )}
                      </div>
                      <span
                        className={`shrink-0 text-[10px] font-medium px-1.5 py-0.5 rounded ${
                          isProspect
                            ? "bg-amber-100 text-amber-700"
                            : "bg-blue-100 text-blue-700"
                        }`}
                      >
                        {isProspect ? "Prospect" : "Contact"}
                      </span>
                    </div>
                  </li>
                );
              }}
              inputMode="search"
              blurOnSelect
              value={null}
              onChange={(ref, newValue) => {
                if (!newValue) return;

                const isProspect = "representatives" in newValue;

                const alreadyAdded = meetingAttendees.some(
                  (a) =>
                    a.type === "Contact" &&
                    a.contact?.contactId === newValue.contactId
                );

                if (alreadyAdded) return;

                setMeetingAttendees((prev) => [
                  ...prev,
                  {
                    email: newValue.email || undefined,
                    name: newValue.name,
                    type: "Contact",
                    // Prospects without email cannot be invited to the calendar event
                    invite: !!newValue.email,
                    contact: newValue,
                  },
                ]);
              }}
              getOptionKey={(o) => o.contactId}
              filterOptions={(_options: MeetingContact[], { inputValue }) => {
                const options = _options.filter(
                  (x) =>
                    !meetingAttendees.some(
                      (a) =>
                        a.type === "Contact" &&
                        a.contact?.contactId === x.contactId
                    )
                );

                const isProspect = (o: MeetingContact) =>
                  "representatives" in o;

                const MAX_OPTIONS = 50;
                if (!inputValue) {
                  // Show contacts with email first, then no-email prospects
                  const withEmail = options
                    .filter((x) => x.email)
                    .slice(0, MAX_OPTIONS);
                  const withoutEmail = options
                    .filter((x) => !x.email && isProspect(x))
                    .slice(0, MAX_OPTIONS - withEmail.length);
                  return [...withEmail, ...withoutEmail];
                }
                const q = inputValue.toLowerCase();
                return options
                  .filter((o) => {
                    const name = (o.name || "").toLowerCase();
                    const email = (o.email || "").toLowerCase();
                    return !!o.name && (name.includes(q) || email.includes(q));
                  })
                  .slice(0, MAX_OPTIONS);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  placeholder="Search contact"
                />
              )}
              ListboxProps={{ style: { maxHeight: 250 } }}
              slotProps={{
                popper: {
                  placement: "bottom-start",
                  sx: {
                    width: "max-content !important",
                    minWidth: 360,
                    maxWidth: "min(560px, 90vw)",
                  },
                },
              }}
              noOptionsText="No matching contacts"
            />

            {/* Add Advisor */}
            <Autocomplete
              options={advisors || []}
              getOptionLabel={(option) => option.name}
              value={null}
              onChange={(_event, newValue) => {
                if (!newValue || !newValue.email) return;

                // Check for duplicates
                if (
                  meetingAttendees.some(
                    (a) =>
                      a.email?.toLowerCase() === newValue.email.toLowerCase()
                  )
                ) {
                  toast.error("Participant already added", {
                    autoClose: 700,
                    hideProgressBar: false,
                    theme: "colored",
                    position: "top-right",
                  });
                  return;
                }

                setMeetingAttendees((prev) => [
                  ...prev,
                  {
                    email: newValue.email,
                    name: newValue.name,
                    type: "Advisor" as const,
                    invite: true,
                    contact: newValue,
                  },
                ]);
              }}
              renderOption={(props, option) => (
                <li {...props} key={option.contactId}>
                  <span className="truncate text-sm">{option.name}</span>
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Select advisor"
                  size="small"
                  fullWidth
                />
              )}
              blurOnSelect
              loading={loadingAdvisors}
              slotProps={{
                popper: {
                  placement: "bottom-start",
                  sx: {
                    width: "max-content !important",
                    minWidth: 280,
                    maxWidth: "min(560px, 90vw)",
                  },
                },
              }}
            />

            {/* Add Manual Email Input */}
            <TextField
              size="small"
              fullWidth
              placeholder="Add by email"
              title="Type an email address and press Enter"
              onBlur={(e) => {
                const email = e.target.value.trim();
                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                if (!email || !emailRegex.test(email)) return;
                setMeetingAttendees((prev) => {
                  if (
                    prev.some(
                      (a) => a.email?.toLowerCase() === email.toLowerCase()
                    )
                  ) {
                    toast.error("Participant already added", {
                      autoClose: 700,
                      hideProgressBar: false,
                      theme: "colored",
                      position: "top-right",
                    });
                    return prev;
                  }
                  return [...prev, { email, type: "Manual", invite: true }];
                });
                e.target.value = "";
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  const input = e.target as HTMLInputElement;
                  const email = input.value.trim();

                  // Basic email validation
                  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                  if (email && emailRegex.test(email)) {
                    // Add email (duplicates will be removed when sending invitation)
                    setMeetingAttendees((prev) => {
                      if (
                        prev.some(
                          (a) => a.email?.toLowerCase() === email.toLowerCase()
                        )
                      ) {
                        toast.error("Participant already added", {
                          autoClose: 700,
                          hideProgressBar: false,
                          theme: "colored",
                          position: "top-right",
                        });
                        return prev;
                      }
                      return [
                        ...prev,
                        {
                          email: email,
                          type: "Manual",
                          invite: true,
                        },
                      ];
                    });
                    input.value = "";
                  } else if (email) {
                    toast.error("Please enter a valid email address", {
                      autoClose: 700,
                      hideProgressBar: false,
                      theme: "colored",
                      position: "top-right",
                    });
                  }
                }
              }}
            />
          </div>

          {/* Participants Table */}
          {meetingAttendees.length > 0 ? (
            <div className="border border-border rounded-lg overflow-hidden">
              <Table
                size="small"
                sx={{ "& .MuiTableCell-root": { py: 0.5, px: 1 } }}
              >
                <TableHead>
                  <TableRow sx={{ height: 32 }}>
                    <TableCell className="font-semibold bg-muted/50">
                      Name
                    </TableCell>
                    <TableCell
                      className="font-semibold bg-muted/50"
                      width="100"
                    >
                      Type
                    </TableCell>
                    <TableCell
                      className="font-semibold bg-muted/50 text-center"
                      width="100"
                    >
                      <div className="flex items-center justify-center gap-1">
                        <Bell className="size-3.5" />
                        <span>Send Invite</span>
                      </div>
                    </TableCell>
                    <TableCell
                      className="font-semibold bg-muted/50 text-center"
                      width="40"
                    ></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {meetingAttendees.map((attendee, index) => {
                    const initials = attendee.name
                      ? attendee.name
                          .split(" ")
                          .map((n) => n.charAt(0))
                          .join("")
                          .toUpperCase()
                          .slice(0, 2)
                      : attendee.email
                      ? attendee.email
                          .split("@")[0]
                          .split(".")
                          .map((n) => n.charAt(0))
                          .join("")
                          .toUpperCase()
                          .slice(0, 2) || attendee.email.charAt(0).toUpperCase()
                      : "?";

                    const noEmail =
                      !attendee.email && attendee.type === "Contact";

                    const isDisabled =
                      attendee.type === "Manual" ||
                      attendee.type === "Organizer" ||
                      noEmail;

                    const isProspect =
                      attendee.type === "Contact" &&
                      !!attendee.contact &&
                      "representatives" in attendee.contact;

                    const contactLabel = isProspect
                      ? "Prospect"
                      : attendee.type;

                    const sharesInvitation =
                      attendee.invite &&
                      !!attendee.email &&
                      meetingAttendees.some(
                        (otherAttendee, otherIndex) =>
                          otherIndex !== index &&
                          otherAttendee.invite &&
                          otherAttendee.email?.toLowerCase() ===
                            attendee.email?.toLowerCase()
                      );

                    return (
                      <TableRow
                        key={`${attendee.email}-${index}`}
                        sx={{ height: 28 }}
                      >
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Avatar
                              sx={{
                                width: 18,
                                height: 18,
                                fontSize: "0.5rem",
                                fontWeight: 500,
                                bgcolor: isProspect
                                  ? "#f59e0b"
                                  : attendee.type === "Contact"
                                  ? "#3b82f6"
                                  : attendee.type === "Organizer"
                                  ? "#10b981"
                                  : attendee.type === "Advisor"
                                  ? "#f59e0b"
                                  : "#6b7280",
                                color: "white",
                              }}
                            >
                              {initials}
                            </Avatar>
                            <div className="min-w-0">
                              <span className="block truncate text-xs font-medium">
                                {attendee.name ||
                                  (attendee.email
                                    ? attendee.email.split("@")[0]
                                    : "?")}
                              </span>
                              <span className="block truncate text-[10px] text-muted-foreground">
                                {noEmail
                                  ? "No email — task only"
                                  : attendee.email}
                              </span>
                            </div>
                            {(() => {
                              if (
                                attendee.type !== "Contact" ||
                                isProspect ||
                                !attendee.contact ||
                                !("contactRepCodes" in attendee.contact)
                              )
                                return null;
                              const fRepIds = (
                                attendee.contact.contactRepCodes || ""
                              )
                                .split(";")
                                .map((s) => s.trim())
                                .filter((s) => s.toUpperCase().startsWith("F"));
                              if (fRepIds.length === 0) return null;
                              return (
                                <button
                                  title="Add representatives"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setRepPopoverAnchor(e.currentTarget);
                                    setRepPopoverIndex(index);
                                  }}
                                  className="flex-shrink-0 ml-1.5 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-50 text-blue-600 border border-blue-200 hover:bg-blue-100 hover:border-blue-400 hover:text-blue-700 transition-colors"
                                >
                                  <UserPlus className="size-2.5" />
                                  <span>Reps</span>
                                </button>
                              );
                            })()}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={contactLabel}
                            size="small"
                            icon={
                              isProspect ? (
                                <UsersIcon className="size-2.5" />
                              ) : attendee.type === "Contact" ? (
                                <UsersIcon className="size-2.5" />
                              ) : attendee.type === "Organizer" ? (
                                <User className="size-2.5" />
                              ) : attendee.type === "Advisor" ? (
                                <Briefcase className="size-2.5" />
                              ) : undefined
                            }
                            sx={{
                              fontSize: "0.55rem",
                              height: "18px",
                              bgcolor: isProspect
                                ? "#fef3c7"
                                : attendee.type === "Contact"
                                ? "#dbeafe"
                                : attendee.type === "Organizer"
                                ? "#d1fae5"
                                : attendee.type === "Advisor"
                                ? "#fef3c7"
                                : "#f3f4f6",
                              color: isProspect
                                ? "#92400e"
                                : attendee.type === "Contact"
                                ? "#1e40af"
                                : attendee.type === "Organizer"
                                ? "#065f46"
                                : attendee.type === "Advisor"
                                ? "#92400e"
                                : "#374151",
                              "& .MuiChip-icon": {
                                marginLeft: "6px",
                                marginRight: "-2px",
                              },
                              "& .MuiChip-label": {
                                px: 0.75,
                              },
                            }}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <div className="flex items-center justify-center">
                            {attendee.type === "Organizer" ? (
                              <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium w-[140px] justify-center cursor-default bg-emerald-100 text-emerald-700">
                                <User className="size-3.5 flex-shrink-0" />
                                <span>Organizer</span>
                              </div>
                            ) : attendee.invite ? (
                              <div
                                onClick={
                                  !isDisabled
                                    ? () => {
                                        setMeetingAttendees((prev) =>
                                          prev.map((a, i) =>
                                            i === index
                                              ? {
                                                  ...a,
                                                  invite: false,
                                                }
                                              : a
                                          )
                                        );
                                      }
                                    : undefined
                                }
                                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium w-[140px] justify-center border-2 ${
                                  isDisabled
                                    ? "cursor-default bg-green-100 text-green-700 border-green-200"
                                    : "cursor-pointer bg-green-100 text-green-700 border-green-300 hover:bg-green-200 hover:border-green-400 hover:shadow-sm transition-all"
                                }`}
                                title={
                                  sharesInvitation
                                    ? "One invitation is sent to this shared email address"
                                    : undefined
                                }
                              >
                                <Bell className="size-3.5 flex-shrink-0" />
                                <span>
                                  {sharesInvitation
                                    ? "Invitation shared"
                                    : "Will be invited"}
                                </span>
                              </div>
                            ) : (
                              <div
                                onClick={
                                  !isDisabled
                                    ? () => {
                                        setMeetingAttendees((prev) =>
                                          prev.map((a, i) =>
                                            i === index
                                              ? {
                                                  ...a,
                                                  invite: true,
                                                }
                                              : a
                                          )
                                        );
                                      }
                                    : undefined
                                }
                                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium w-[140px] justify-center border-2 ${
                                  isDisabled
                                    ? "cursor-default bg-gray-100 text-gray-600 border-gray-200"
                                    : "cursor-pointer bg-gray-100 text-gray-600 border-gray-300 hover:bg-gray-200 hover:border-gray-400 hover:shadow-sm transition-all"
                                }`}
                              >
                                <BellOff className="size-3.5 flex-shrink-0" />
                                <span>Not invited</span>
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell align="center">
                          {attendee.type !== "Organizer" && (
                            <Button
                              variant="icon"
                              size="xs"
                              onClick={() => {
                                setMeetingAttendees((prev) =>
                                  prev.filter((_, i) => i !== index)
                                );
                              }}
                              className="hover:bg-red-50 opacity-70 hover:opacity-100 transition-opacity size-5"
                            >
                              <Trash2 className="size-3 text-red-500" />
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-6 text-gray-500 text-sm border border-dashed border-gray-300 rounded-lg">
              No participants added yet
            </div>
          )}

          {/* Representatives popover */}
          {(() => {
            const attendee =
              repPopoverIndex !== null
                ? meetingAttendees[repPopoverIndex]
                : null;
            const fRepIds =
              attendee &&
              attendee.contact &&
              "contactRepCodes" in attendee.contact
                ? (attendee.contact.contactRepCodes || "")
                    .split(";")
                    .map((s) => s.trim())
                    .filter((s) => s.toUpperCase().startsWith("F"))
                : [];

            return (
              <Popover
                open={!!repPopoverAnchor}
                anchorEl={repPopoverAnchor}
                onClose={() => {
                  setRepPopoverAnchor(null);
                  setRepPopoverIndex(null);
                }}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "left",
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "left",
                }}
                PaperProps={{
                  sx: {
                    borderRadius: 3,
                    boxShadow: "0 8px 30px rgba(0,0,0,0.12)",
                    minWidth: 280,
                    overflow: "hidden",
                  },
                }}
              >
                {/* Header */}
                <div className="flex items-center gap-2 px-4 py-3 border-b border-border bg-muted/40">
                  <UsersIcon className="size-3.5 text-blue-500 flex-shrink-0" />
                  <span className="text-xs font-semibold text-foreground">
                    Representatives
                  </span>
                  {attendee?.name && (
                    <span className="ml-auto text-[10px] text-muted-foreground truncate max-w-[120px]">
                      {attendee.name}
                    </span>
                  )}
                  <button
                    onClick={() => {
                      setRepPopoverAnchor(null);
                      setRepPopoverIndex(null);
                    }}
                    className="ml-auto flex-shrink-0 rounded p-0.5 hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
                  >
                    <X className="size-3.5" />
                  </button>
                </div>

                <div className="p-2">
                  {fRepIds.length === 0 ? (
                    <p className="text-xs text-muted-foreground px-2 py-2">
                      No representatives found
                    </p>
                  ) : (
                    <div className="flex flex-col gap-0.5">
                      {fRepIds.map((repId) => {
                        const repContact = contacts.find(
                          (c) => c.contactId === repId
                        );

                        // Rep exists but user has no access to them
                        if (!repContact) {
                          return (
                            <div
                              key={repId}
                              className="flex items-center gap-3 rounded-lg px-2 py-2 opacity-50 cursor-not-allowed"
                              title="Du har ikke tilgang til denne kontakten"
                            >
                              <Avatar
                                sx={{
                                  width: 26,
                                  height: 26,
                                  fontSize: "0.55rem",
                                  fontWeight: 600,
                                  bgcolor: "#94a3b8",
                                  color: "white",
                                  flexShrink: 0,
                                }}
                              >
                                ?
                              </Avatar>
                              <div className="flex flex-col min-w-0 flex-1">
                                <span className="text-xs font-medium text-muted-foreground leading-tight">
                                  {repId}
                                </span>
                                <span className="text-[10px] text-muted-foreground/70 leading-tight">
                                  Ingen tilgang
                                </span>
                              </div>
                              <Lock className="size-3 text-muted-foreground flex-shrink-0" />
                            </div>
                          );
                        }

                        // Only match by contactId â€” emails may legitimately overlap (e.g. organizer)
                        const alreadyAdded = meetingAttendees.some(
                          (a) => a.contact?.contactId === repId
                        );
                        const initials = repContact.name
                          ? repContact.name
                              .split(" ")
                              .map((n) => n.charAt(0))
                              .join(" ")
                              .toUpperCase()
                              .slice(0, 2)
                          : "?";
                        return (
                          <label
                            key={repId}
                            className={`flex items-center gap-3 cursor-pointer rounded-lg px-2 py-2 transition-colors ${
                              alreadyAdded
                                ? "bg-blue-50 dark:bg-blue-950/30"
                                : "hover:bg-muted/60"
                            }`}
                          >
                            <Checkbox
                              size="small"
                              checked={alreadyAdded}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  if (alreadyAdded) return;
                                  setMeetingAttendees((prev) => [
                                    ...prev,
                                    {
                                      email: repContact.email || undefined,
                                      name: repContact.name,
                                      type: "Contact",
                                      invite: !!repContact.email,
                                      contact: repContact,
                                    },
                                  ]);
                                } else {
                                  // Remove only the specific entry by contactId
                                  setMeetingAttendees((prev) =>
                                    prev.filter(
                                      (a) => a.contact?.contactId !== repId
                                    )
                                  );
                                }
                              }}
                              sx={{ p: 0, mr: 0 }}
                            />
                            <Avatar
                              sx={{
                                width: 26,
                                height: 26,
                                fontSize: "0.55rem",
                                fontWeight: 600,
                                lineHeight: 1,
                                bgcolor: alreadyAdded ? "#3b82f6" : "#94a3b8",
                                color: "white",
                                flexShrink: 0,
                              }}
                            >
                              {initials}
                            </Avatar>
                            <div className="flex flex-col min-w-0">
                              <span className="text-xs font-medium truncate leading-tight">
                                {repContact.name}
                              </span>
                              {repContact.email ? (
                                <span className="text-[10px] text-muted-foreground truncate leading-tight">
                                  {repContact.email}
                                </span>
                              ) : (
                                <span className="text-[10px] text-amber-500 leading-tight">
                                  No email â€” task only
                                </span>
                              )}
                            </div>
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>
              </Popover>
            );
          })()}
        </div>
      ),
      [
        meetingAttendees,
        contacts,
        contactsLoading,
        advisors,
        loadingAdvisors,
        createTask,
        repPopoverAnchor,
        repPopoverIndex,
      ]
    );

    // Get linked contacts for task creation (only Contact type)
    const getLinkedContacts = (): (MeetingContact | Advisor)[] => {
      return meetingAttendees
        .filter((a) => ["Contact", "Advisor"].includes(a.type) && a.contact)
        .map((a) => a.contact!)
        .filter(
          (c, i, arr) => arr.findIndex((x) => x.contactId === c.contactId) === i
        );
    };

    const getProfile = useCallback(async () => {
      const token = await getToken();
      if (!token) return undefined;
      return await getMyProfile(token);
    }, [getToken]);

    // Never throws: a failed login becomes `authError` so the dialog can still open.
    const ensureOrganizer = useCallback(async () => {
      try {
        const profile = await getProfile();
        if (!profile?.mail) throw new Error("Microsoft profile not found");
        setAuthError(null);
        return profile;
      } catch (error) {
        console.error("Could not load Microsoft profile:", error);
        setAuthError(
          error instanceof Error ? error.message : "Microsoft login failed"
        );
        return undefined;
      }
    }, [getProfile]);

    const retryLogin = async () => {
      resetCooldown();
      const profile = await ensureOrganizer();
      if (!profile?.mail) return;
      setMyEmail(profile.mail);
      setMyName(profile.displayName);
      setMeetingAttendees((prev) =>
        prev.some((a) => a.type === "Organizer")
          ? prev
          : [
              {
                email: profile.mail,
                name: profile.displayName || profile.mail,
                type: "Organizer",
                invite: true,
              },
              ...prev,
            ]
      );
    };

    const handleChange = (
      field: keyof CalendarEventDetails,
      value: CalendarEventDetails[keyof CalendarEventDetails]
    ) => {
      setMeeting((prev) => ({ ...prev, [field]: value }));
    };

    const handleDateChange = (field: "start" | "end", value: string) => {
      const localDateTime = dayjs(value).format("YYYY-MM-DDTHH:mm:ss");
      setMeeting((prev) => {
        const newMeeting = {
          ...prev,
          [field]: {
            dateTime: localDateTime,
            timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          },
        };

        // If start time is changed, preserve the original duration
        if (field === "start" && prev.end?.dateTime && prev.start?.dateTime) {
          const oldStart = dayjs(prev.start.dateTime);
          const oldEnd = dayjs(prev.end.dateTime);
          const durationMinutes = oldEnd.diff(oldStart, "minute");
          const newStart = dayjs(localDateTime);

          // Preserve duration (minimum 30 minutes)
          const newEnd = newStart.add(Math.max(durationMinutes, 30), "minute");
          newMeeting.end = {
            dateTime: newEnd.format("YYYY-MM-DDTHH:mm:ss"),
            timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          };
        }

        // If end time is changed and becomes before start time, adjust to be after start
        if (field === "end" && prev.start?.dateTime) {
          const currentStart = dayjs(prev.start.dateTime);
          const newEnd = dayjs(localDateTime);

          if (newEnd.isBefore(currentStart) || newEnd.isSame(currentStart)) {
            // Set end time to 30 minutes after start time
            newMeeting.end = {
              dateTime: currentStart
                .add(30, "minute")
                .format("YYYY-MM-DDTHH:mm:ss"),
              timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            };
          }
        }

        return newMeeting;
      });
    };

    // Stable reference (no deps) so it doesn't break ScheduleAssistant's memoization.
    const handleTimeChange = useCallback((startTime: Date, endTime: Date) => {
      const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      setMeeting((prev) => ({
        ...prev,
        start: {
          dateTime: dayjs(startTime).format("YYYY-MM-DDTHH:mm:ss"),
          timeZone: prev.start?.timeZone || timeZone,
        },
        end: {
          dateTime: dayjs(endTime).format("YYYY-MM-DDTHH:mm:ss"),
          timeZone: prev.end?.timeZone || timeZone,
        },
      }));
    }, []);

    const resetForm = () => {
      setRoom("");
      setMeetingAttendees([]);
      setHasConflict(false);
      setEditingEventId(null);
      setCategory("Meeting");
      setMeetingTypeSelected(false);
      setActiveTab("details");
      setMeeting({
        subject: "",
        start: {
          dateTime: dayjs().format("YYYY-MM-DDTHH:mm:ss"),
          timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        },
        end: {
          dateTime: dayjs().add(30, "minute").format("YYYY-MM-DDTHH:mm:ss"),
          timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        },
        body: { contentType: "html", content: "" },
        attendees: [],
        isOnlineMeeting: true,
        onlineMeetingProvider: "teamsForBusiness",
        categories: ["Meeting"],
      });
    };

    const handleClose = () => {
      setOpen(false);
      resetForm();
    };

    const handleSubmit = async () => {
      if (
        !meeting.subject ||
        !meeting.start ||
        !meeting.end ||
        !meetingTypeSelected
      ) {
        toast.error("Please fill in all required fields");
        return;
      }

      // Validate that end time is after start time
      const startTime = dayjs(meeting.start.dateTime);
      const endTime = dayjs(meeting.end.dateTime);

      if (endTime.isBefore(startTime) || endTime.isSame(startTime)) {
        toast.error("End time must be after start time");
        return;
      }

      setLoading(true);
      try {
        const token = await getToken();

        const eventDetails: CalendarEventDetails = {
          ...meeting,
          attendees: getAllAttendees().map((email) => ({
            emailAddress: {
              address: email,
              name: email.split("@")[0],
            },
            type: "required" as const,
            status: {
              response: "none" as const,
              time: new Date().toISOString(),
            },
          })),
        };

        let createdOrUpdatedEvent;

        // Update existing event or create new one
        if (editingEventId) {
          await updateCalendarEvent({
            accessToken: token,
            eventId: editingEventId,
            subject: meeting.subject,
            startDateTime: meeting.start.dateTime,
            endDateTime: meeting.end.dateTime,
            timeZone: meeting.start.timeZone,
            location: meeting.location?.displayName,
            body: meeting.body?.content,
            bodyContentType:
              meeting.body?.contentType === "html" ? "html" : "text",
            attendees: eventDetails.attendees?.map((att) => ({
              emailAddress: att.emailAddress,
              type: att.type || "required",
            })),
            categories: [category],
            isOnlineMeeting: meeting.isOnlineMeeting,
            onlineMeetingProvider: meeting.onlineMeetingProvider as
              | "teamsForBusiness"
              | "skypeForBusiness"
              | "skypeForConsumer"
              | undefined,
            isReminderOn: meeting.isReminderOn,
            reminderMinutesBeforeStart: meeting.reminderMinutesBeforeStart,
          });
          toast.success("Meeting updated successfully!", {
            autoClose: 2000,
            hideProgressBar: false,
            position: "top-right",
            theme: "colored",
          });
        } else {
          createdOrUpdatedEvent = await createEvent(token, eventDetails);
          toast.success("Meeting created successfully!", {
            autoClose: 2000,
            hideProgressBar: false,
            position: "top-right",
            theme: "colored",
          });
        }

        // Create task automatically when contacts are linked (only for new events)
        const linkedContacts = getLinkedContacts();

        if (editingEventId && relatedTask && userProfile?.username) {
          try {
            const _dur = dayjs.duration(
              dayjs(meeting.end.dateTime).diff(
                dayjs(meeting.start.dateTime),
                "minutes"
              ),
              "minutes"
            );
            const assignees = Array.from(
              new Set(
                [
                  userProfile.username,
                  ...linkedContacts.map((c) => c.contactId),
                ].filter(Boolean)
              )
            );
            await saveTask(
              assignees,
              {
                taskId: relatedTask.id,
                title: meeting.subject,
                type: "CRM-" + category,
                assignee: userProfile.username,
                owner: userProfile.username,
                priority: Number(relatedTask.priority) || 50,
                category: relatedTask.category || "",
                dueDate: dayjs(meeting.start.dateTime).format(
                  "YYYY-MM-DD HH:mm:ss"
                ),
                durationTime: _dur.format("HH:mm"),
                description:
                  (meeting.body?.contentType === "html" && meeting.body.content
                    ? htmlToText(meeting.body.content)
                    : meeting.body?.content) ||
                  relatedTask.description ||
                  "",
                reference: relatedTask.reference || "",
              },
              () => {
                /* task updated */
              }
            );
          } catch (error) {
            console.error("Failed to update task:", error);
          }
        }

        if (
          !editingEventId &&
          createTask &&
          userProfile?.username &&
          createdOrUpdatedEvent
        ) {
          try {
            const _dur = dayjs.duration(
              dayjs(meeting.end.dateTime).diff(
                dayjs(meeting.start.dateTime),
                "minutes"
              ),
              "minutes"
            );
            // Only include user and linked contacts, not meeting rooms
            const assignees = Array.from(
              new Set(
                [
                  userProfile.username,
                  ...linkedContacts.map((c) => c.contactId),
                ].filter(Boolean)
              )
            );
            await saveTask(
              assignees,
              {
                title: meeting.subject,
                type: "CRM-" + category,
                assignee: userProfile.username,
                owner: userProfile.username,
                priority: 50,
                category: "",
                dueDate: meeting.start?.dateTime
                  ? dayjs(meeting.start.dateTime).format("YYYY-MM-DD HH:mm:ss")
                  : dayjs().format("YYYY-MM-DD HH:mm:ss"),
                durationTime: _dur.format("HH:mm"),
                description:
                  meeting.body?.contentType === "html" && meeting.body.content
                    ? htmlToText(meeting.body.content)
                    : meeting.body?.content || "",
                reference: createdOrUpdatedEvent.iCalUId || "",
              },
              () => {
                // Task created successfully
              }
            );
          } catch (error) {
            console.error("Failed to create task:", error);
          }
        }

        handleClose();
        onSuccess?.();
      } catch (error) {
        console.error("Failed to save meeting:", error);
        toast.error(
          editingEventId
            ? "Failed to update meeting"
            : "Failed to create meeting"
        );
      } finally {
        setLoading(false);
      }
    };

    const handleDelete = async () => {
      if (!editingEventId) return;

      const confirmDelete = window.confirm(
        "Are you sure you want to cancel this meeting? This action cannot be undone."
      );

      if (!confirmDelete) return;

      setLoading(true);
      try {
        const token = await getToken();
        await deleteEvent({
          accessToken: token,
          eventId: editingEventId,
        });

        toast.success("Meeting cancelled successfully!", {
          autoClose: 2000,
          hideProgressBar: false,
          position: "top-right",
          theme: "colored",
        });
        handleClose();
        onSuccess?.();
      } catch (error) {
        console.error("Failed to cancel meeting:", error);
        toast.error("Failed to cancel meeting");
      } finally {
        setLoading(false);
      }
    };

    const handleOpenNewMeeting = async (
      time: Dayjs,
      resource?: string,
      prefilledContactIds?: string[],
      eventToEdit?: CalendarEvent,
      endTime?: Dayjs,
      relatedTask?: QueryTask
    ) => {
      // Close dialog first to ensure useEffect triggers on reopen
      if (open) {
        setOpen(false);
        await new Promise((resolve) => setTimeout(resolve, 50));
      }

      resetForm();
      setOpen(true);

      if (relatedTask) setRelatedTask(relatedTask);

      // If editing an existing event, populate form with event data
      if (eventToEdit && eventToEdit.id) {
        const hasRegularAttendee = eventToEdit.attendees?.some((attendee) => {
          const email = attendee.emailAddress?.address;
          return email && !MEETING_ROOMS.some((room) => room.email === email);
        });

        // Events opened from a room calendar can omit the real attendee list.
        // Resolve the user's own copy before rebuilding the dialog state.
        if (!hasRegularAttendee && eventToEdit.iCalUId && eventToEdit.start) {
          try {
            const token = await getToken();
            const calendarEvents = await getCalendarEvents(
              token,
              dayjs(eventToEdit.start.dateTime)
                .subtract(1, "day")
                .format("YYYY-MM-DD"),
              dayjs(eventToEdit.end?.dateTime ?? eventToEdit.start.dateTime)
                .add(1, "day")
                .format("YYYY-MM-DD"),
              true
            );
            const ownEvent = calendarEvents.find(
              (event) => event.iCalUId === eventToEdit!.iCalUId
            );
            if (ownEvent?.id) eventToEdit = ownEvent;
          } catch (error) {
            console.error("Failed to load the user's event attendees:", error);
          }
        }

        setEditingEventId(eventToEdit.id ?? null);
        setMeetingTypeSelected(true); // Mark meeting type as selected when editing
        const organizerEmail =
          eventToEdit.organizer?.emailAddress?.address || "";

        // Extract room from attendees
        const roomAttendee = eventToEdit.attendees?.find((att) =>
          MEETING_ROOMS.some((room) => room.email === att.emailAddress?.address)
        );
        if (roomAttendee?.emailAddress?.address) {
          setRoom(roomAttendee.emailAddress.address);
        }

        const category = eventToEdit.categories;
        if (category && category.length > 0) {
          setCategory(category[0]);
        }

        // Build unified attendees list with types
        const newAttendees: MeetingAttendee[] = [];
        const profile = await ensureOrganizer();

        // Add organizer
        if (profile) {
          newAttendees.push({
            email: profile.mail,
            name: profile.displayName || profile.mail,
            type: "Organizer",
            invite: true,
          });
        }

        // Keep task contact IDs so Graph aliases can still be linked to CRM identities.
        let taskLinkedContacts: string[] = [];
        if (eventToEdit.iCalUId && relatedTask) {
          try {
            if (relatedTask) {
              // contactMap is stored as email|contactId pairs.
              if (relatedTask.contactMap) {
                try {
                  const contactMapObj = relatedTask.contactMap
                    .split(",")
                    .map((x) => {
                      const arr = x.split("|");
                      return arr[1];
                    });
                  taskLinkedContacts = contactMapObj;
                } catch (e) {
                  console.error("Failed to parse contactMap:", e);
                }
              }
            }
          } catch (error) {
            console.error("Failed to fetch related task:", error);
          }
        }

        // Extract all attendees (excluding rooms and self)
        const selfEmail = (profile?.mail ?? myEmail).toLowerCase();
        const regularAttendees =
          eventToEdit.attendees
            ?.filter((att) => {
              const email = att.emailAddress?.address;
              return (
                email &&
                !MEETING_ROOMS.some((room) => room.email === email) &&
                email.toLowerCase() !== selfEmail
              );
            })
            .map((att) => ({
              email: att.emailAddress.address!,
              name: att.emailAddress.name,
            })) || [];

        const normalizeDisplayName = (name?: string) =>
          (name ?? "").trim().replace(/\s+/g, " ").toLowerCase();
        const taskLinkedContactsByName = (name?: string) => {
          const normalizedName = normalizeDisplayName(name);
          if (!normalizedName) return [];

          return contacts.filter(
            (contact) =>
              taskLinkedContacts.includes(contact.contactId) &&
              normalizeDisplayName(contact.name) === normalizedName
          );
        };
        const taskLinkedAdvisorsByName = (name?: string) => {
          const normalizedName = normalizeDisplayName(name);
          if (!normalizedName) return [];

          return advisors.filter(
            (advisor) =>
              taskLinkedContacts.includes(advisor.contactId) &&
              normalizeDisplayName(advisor.name) === normalizedName
          );
        };

        // Prefer email matches, then task-linked CRM identities with the same
        // display name. Graph can return a user's primary alias instead of the
        // address stored in the task's contactMap.
        for (const att of regularAttendees) {
          const taskContactsWithName = taskLinkedContactsByName(att.name);
          const taskAdvisorsWithName = taskLinkedAdvisorsByName(att.name);
          const matchedContact =
            contacts.find(
              (c) => c.email?.toLowerCase() === att.email.toLowerCase()
            ) ??
            (taskContactsWithName.length === 1
              ? taskContactsWithName[0]
              : undefined);
          const matchedAdvisor =
            !matchedContact &&
            (advisors.find(
              (a) => a.email?.toLowerCase() === att.email.toLowerCase()
            ) ??
              (taskAdvisorsWithName.length === 1
                ? taskAdvisorsWithName[0]
                : undefined));

          if (matchedContact) {
            newAttendees.push({
              email: att.email,
              name: matchedContact.name || att.name,
              type: "Contact",
              invite: true,
              contact: matchedContact,
            });
          } else if (matchedAdvisor) {
            newAttendees.push({
              email: att.email,
              name: matchedAdvisor.name || att.name,
              type: "Advisor",
              invite: true,
              contact: matchedAdvisor,
            });
          } else {
            newAttendees.push({
              email: att.email,
              name: att.name,
              type: "Manual",
              invite: true,
            });
          }
        }

        // Add task linked contacts that are not already in attendees
        for (const contactId of taskLinkedContacts) {
          const contact = contacts.find((c) => c.contactId === contactId);
          const advisor = advisors.find((a) => a.contactId === contactId);

          if (contact && contact.email) {
            const exists = newAttendees.some(
              (a) =>
                a.type === "Contact" &&
                a.contact?.contactId === contact.contactId
            );
            if (!exists) {
              newAttendees.push({
                email: contact.email,
                name: contact.name,
                type: "Contact",
                invite: contact.email === organizerEmail, // Not invited to meeting but linked to task
                contact: contact,
              });
            }
          } else if (advisor && advisor.email) {
            const exists = newAttendees.some(
              (a) =>
                a.type === "Advisor" &&
                a.contact?.contactId === advisor.contactId
            );
            if (!exists) {
              newAttendees.push({
                email: advisor.email,
                name: advisor.name,
                type: "Advisor",
                invite:
                  advisor.email === organizerEmail ||
                  !!eventToEdit.attendees?.some(
                    (a) =>
                      a.emailAddress.address === advisor.email.toLowerCase()
                  ),
                contact: advisor,
              });
            }
          }
        }

        // Add prefilled contacts if provided (in edit mode)
        if (
          prefilledContactIds &&
          prefilledContactIds.length > 0 &&
          !contactsLoading
        ) {
          for (const contactId of prefilledContactIds) {
            const contact = contacts.find((c) => c.contactId === contactId);
            if (contact && contact.email) {
              // Check if not already added
              const exists = newAttendees.some(
                (a) =>
                  a.type === "Contact" &&
                  a.contact?.contactId === contact.contactId
              );
              if (!exists) {
                newAttendees.push({
                  email: contact.email,
                  name: contact.name,
                  type: "Contact",
                  invite: true,
                  contact: contact,
                });
              }
            }
          }
        }
        setMeetingAttendees(newAttendees);

        const meetingData: CalendarEventDetails = {
          subject: eventToEdit.subject || "",
          start: {
            dateTime: eventToEdit.start?.dateTime
              ? dayjs(eventToEdit.start.dateTime).format("YYYY-MM-DDTHH:mm:ss")
              : time.format("YYYY-MM-DDTHH:mm:ss"),
            timeZone:
              eventToEdit.start?.timeZone ||
              Intl.DateTimeFormat().resolvedOptions().timeZone,
          },
          end: {
            dateTime: eventToEdit.end?.dateTime
              ? dayjs(eventToEdit.end.dateTime).format("YYYY-MM-DDTHH:mm:ss")
              : time.add(30, "minute").format("YYYY-MM-DDTHH:mm:ss"),
            timeZone:
              eventToEdit.end?.timeZone ||
              Intl.DateTimeFormat().resolvedOptions().timeZone,
          },
          body: {
            contentType: "html",
            content: eventToEdit.body?.content || eventToEdit.bodyPreview || "",
          },
          location: eventToEdit.location,
          attendees: [],
          isOnlineMeeting: eventToEdit.isOnlineMeeting ?? false,
          onlineMeetingProvider: eventToEdit.onlineMeetingProvider as
            | "teamsForBusiness"
            | "skypeForBusiness"
            | "skypeForConsumer"
            | undefined,
          isReminderOn:
            (eventToEdit as CalendarEventDetails).isReminderOn ?? true,
          reminderMinutesBeforeStart:
            (eventToEdit as CalendarEventDetails).reminderMinutesBeforeStart ??
            15,
        };

        setMeeting(meetingData);
        return;
      }

      // Normal new meeting flow
      setEditingEventId(null);

      if (resource) setRoom(resource);

      // Initialize with organizer
      const newAttendees: MeetingAttendee[] = [];
      const profile = await ensureOrganizer();
      if (profile) {
        newAttendees.push({
          email: profile.mail,
          name: profile.displayName || profile.mail,
          type: "Organizer",
          invite: true,
        });
      }

      // Pre-populate contacts if contactIds provided
      if (
        prefilledContactIds &&
        prefilledContactIds.length > 0 &&
        !contactsLoading
      ) {
        for (const contactId of prefilledContactIds) {
          const contact = contacts.find((c) => c.contactId === contactId);
          if (contact) {
            newAttendees.push({
              email: contact.email || undefined,
              name: contact.name,
              type: "Contact",
              // Prospects without email cannot receive a calendar invite
              invite: !!contact.email,
              contact: contact,
            });
          }
        }
      }

      setMeetingAttendees(newAttendees);

      setMeeting({
        subject: "",
        start: {
          dateTime: time.format("YYYY-MM-DDTHH:mm:ss"),
          timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        },
        end: {
          dateTime: (endTime || time.add(30, "minute")).format(
            "YYYY-MM-DDTHH:mm:ss"
          ),
          timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        },
        body: { contentType: "html", content: "" },
        attendees: [],
        isOnlineMeeting: true,
        onlineMeetingProvider: "teamsForBusiness",
        categories: ["Meeting"],
      });
    };

    const titleRef = useRef<HTMLInputElement>(null);

    useImperativeHandle(ref, () => ({
      open: handleOpenNewMeeting,
      close: handleClose,
    }));

    return (
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="xl"
        fullWidth
        onTransitionEnter={() => {
          titleRef.current?.focus();
        }}
        PaperProps={{
          sx: {
            width: "min(96vw, 1440px)",
            maxWidth: "1440px",
            borderRadius: 2,
            bgcolor: "hsl(var(--card))",
          },
        }}
      >
        {/* Header */}
        <div className="flex shrink-0 items-center justify-between px-6 py-4 border-b border-border">
          <div className="flex items-center gap-2">
            <div className="p-2 rounded-lg bg-primary/10">
              <CalendarIcon className="size-5 text-primary" />
            </div>
            <h2 className="text-lg font-semibold">
              {editingEventId ? "Edit Event" : "New Event"}
            </h2>
          </div>
          <Button onClick={handleClose} size="sm" variant="icon">
            <X className="size-3.5" />
          </Button>
        </div>

        <DialogContent
          className={`flex flex-col !pl-0 !pt-0 ${
            activeTab === "details"
              ? "min-h-[min(50vh,640px)] overflow-y-auto"
              : "h-[min(50vh,640px)] overflow-hidden"
          }`}
        >
          {authError && (
            <div
              role="alert"
              title={authError}
              className="flex shrink-0 items-center gap-2 border-b border-amber-200 bg-amber-50 px-6 py-2 text-xs text-amber-800 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-200"
            >
              <AlertCircle className="size-4 shrink-0" />
              <span className="min-w-0 flex-1">
                You are not signed in to Microsoft. You are not added as a
                participant, and availability and invitations are unavailable.
              </span>
              <Button size="sm" variant="outline" onClick={retryLogin}>
                Sign in again
              </Button>
            </div>
          )}
          {/* Tabs */}
          <div className="flex shrink-0 items-center gap-1 border-b border-border px-6 pt-0">
            <button
              type="button"
              onClick={() => setActiveTab("details")}
              className={`inline-flex items-center gap-1.5 rounded-t-md px-3 py-2 text-sm font-medium transition-colors ${
                activeTab === "details"
                  ? "border-b-2 border-primary text-primary"
                  : "border-b-2 border-transparent text-muted-foreground hover:text-foreground"
              }`}
            >
              <FileText className="size-3.5" />
              Details
            </button>
            <button
              type="button"
              onClick={() => setActiveTab("availability")}
              className={`inline-flex items-center gap-1.5 rounded-t-md px-3 py-2 text-sm font-medium transition-colors ${
                activeTab === "availability"
                  ? "border-b-2 border-primary text-primary"
                  : "border-b-2 border-transparent text-muted-foreground hover:text-foreground"
              }`}
            >
              <Clock className="size-3.5" />
              Availability
              {hasConflict && (
                <span className="size-1.5 rounded-full bg-amber-500" />
              )}
            </button>
          </div>

          {activeTab === "details" ? (
            <div className="grid shrink-0 grid-cols-1 gap-6 p-6 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
              <div className="space-y-2 lg:pr-1">
                {/* Title */}
                <TextField
                  autoFocus
                  fullWidth
                  size="small"
                  inputRef={titleRef}
                  placeholder="Meeting title"
                  value={meeting.subject}
                  onChange={(e) => handleChange("subject", e.target.value)}
                  error={!meeting.subject}
                  helperText={!meeting.subject && "Title is required"}
                />

                {/* Time & Category */}
                <div className="grid grid-cols-3 gap-3">
                  <LocalizationProvider
                    dateAdapter={AdapterDayjs}
                    adapterLocale="nb"
                  >
                    <DateTimePicker
                      label="Start"
                      value={dayjs(meeting.start?.dateTime)}
                      onChange={(newValue) =>
                        handleDateChange("start", newValue?.toISOString() || "")
                      }
                      slotProps={{
                        textField: { size: "small", fullWidth: true },
                      }}
                    />
                  </LocalizationProvider>

                  <LocalizationProvider
                    dateAdapter={AdapterDayjs}
                    adapterLocale="nb"
                  >
                    <DateTimePicker
                      label="End"
                      value={dayjs(meeting.end?.dateTime)}
                      onChange={(newValue) =>
                        handleDateChange("end", newValue?.toISOString() || "")
                      }
                      slotProps={{
                        textField: { size: "small", fullWidth: true },
                      }}
                    />
                  </LocalizationProvider>

                  <Select
                    value={category}
                    size="small"
                    fullWidth
                    onChange={(e) => {
                      const newCategory = e.target.value;
                      setCategory(newCategory);
                      setMeeting((prev) => ({
                        ...prev,
                        categories: [newCategory],
                      }));
                    }}
                  >
                    <MenuItem value="Meeting">
                      <div className="flex items-center gap-2">
                        <CalendarIcon className="size-4 text-blue-500" />
                        <span>Meeting</span>
                      </div>
                    </MenuItem>
                    <MenuItem value="Phone">
                      <div className="flex items-center gap-2">
                        <Phone className="size-4 text-green-500" />
                        <span>Phone</span>
                      </div>
                    </MenuItem>
                    <MenuItem value="Task">
                      <div className="flex items-center gap-2">
                        <FileText className="size-4 text-orange-500" />
                        <span>Task</span>
                      </div>
                    </MenuItem>
                    <MenuItem value="Private">
                      <div className="flex items-center gap-2">
                        <Lock className="size-4 text-purple-500" />
                        <span>Private</span>
                      </div>
                    </MenuItem>
                  </Select>
                </div>

                {/* Meeting Type, Location & Reminder */}
                <div className="grid grid-cols-3 gap-3">
                  <Select
                    value={
                      meetingTypeSelected
                        ? meeting.isOnlineMeeting
                          ? "teams"
                          : "none"
                        : ""
                    }
                    size="small"
                    fullWidth
                    displayEmpty
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === "teams" || value === "none") {
                        setMeetingTypeSelected(true);
                        const isTeams = value === "teams";
                        handleChange("isOnlineMeeting", isTeams);
                        handleChange(
                          "onlineMeetingProvider",
                          isTeams ? "teamsForBusiness" : undefined
                        );
                      }
                    }}
                    startAdornment={
                      <Video className="size-4 text-muted-foreground mr-2" />
                    }
                    error={!meetingTypeSelected}
                  >
                    <MenuItem value="" disabled>
                      <em>Select meeting type</em>
                    </MenuItem>
                    <MenuItem value="teams">Teams Meeting</MenuItem>
                    <MenuItem value="none">No online meeting</MenuItem>
                  </Select>

                  <Select
                    value={room}
                    size="small"
                    fullWidth
                    displayEmpty
                    onChange={(e) => setRoom(e.target.value)}
                    startAdornment={
                      <MapPin className="size-4 text-muted-foreground mr-2" />
                    }
                  >
                    <MenuItem value="">
                      <em>No meeting room</em>
                    </MenuItem>
                    {MEETING_ROOMS.map((r) => (
                      <MenuItem key={r.email} value={r.email}>
                        {r.name}
                      </MenuItem>
                    ))}
                  </Select>

                  <Select
                    value={
                      meeting.isReminderOn === false
                        ? "off"
                        : String(meeting.reminderMinutesBeforeStart ?? 15)
                    }
                    size="small"
                    fullWidth
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === "off") {
                        handleChange("isReminderOn", false);
                      } else {
                        handleChange("isReminderOn", true);
                        handleChange(
                          "reminderMinutesBeforeStart",
                          Number(value)
                        );
                      }
                    }}
                    startAdornment={
                      meeting.isReminderOn === false ? (
                        <BellOff className="size-4 text-muted-foreground mr-2" />
                      ) : (
                        <Bell className="size-4 text-muted-foreground mr-2" />
                      )
                    }
                  >
                    <MenuItem value="off">No reminder</MenuItem>
                    <MenuItem value={0}>At event time</MenuItem>
                    <MenuItem value={5}>5 min before</MenuItem>
                    <MenuItem value={15}>15 min before</MenuItem>
                    <MenuItem value={30}>30 min before</MenuItem>
                    <MenuItem value={60}>1 hour before</MenuItem>
                    <MenuItem value={120}>2 hours before</MenuItem>
                    <MenuItem value={1440}>1 day before</MenuItem>
                  </Select>
                </div>

                {/* Description */}
                <div className="space-y-2">
                  <label className="text-sm font-medium flex items-center gap-2">
                    <FileText className="size-4" />
                    Description
                  </label>
                  <MeetingDescriptionEditor
                    value={meeting.body?.content || ""}
                    onChange={(html) =>
                      handleChange("body", {
                        contentType: "html",
                        content: html,
                      })
                    }
                    disabled={loading}
                  />
                </div>
              </div>
              <div className="lg:border-l lg:border-border lg:pl-6">
                {participantsSection}
              </div>
            </div>
          ) : (
            <div className="min-h-0 flex-1 overflow-hidden p-6">
              {scheduleAttendees.length > 0 && !authError ? (
                <Suspense
                  fallback={
                    <div className="flex h-full min-h-40 items-center justify-center">
                      <LogoAnimation size={24} variant="spin" />
                    </div>
                  }
                >
                  <ScheduleAssistant
                    start={scheduleStart}
                    end={scheduleEnd}
                    attendees={scheduleAttendees}
                    onTimeChange={handleTimeChange}
                    onConflictChange={setHasConflict}
                  />
                </Suspense>
              ) : (
                <div className="flex h-full min-h-40 items-center justify-center text-center text-xs text-muted-foreground">
                  {authError
                    ? "Sign in to Microsoft to see availability."
                    : "Add participants to see weekly availability."}
                </div>
              )}
            </div>
          )}
        </DialogContent>

        {/* Footer */}
        <div className="flex shrink-0 items-center justify-between px-6 py-4 border-t border-border bg-muted/20">
          <div>
            {hasConflict && (
              <div className="flex items-center gap-2 text-amber-600 dark:text-amber-400">
                <AlertCircle className="size-4" />
                <span className="text-sm font-medium">
                  Scheduling conflict detected
                </span>
              </div>
            )}
          </div>
          <div className="flex gap-2">
            {editingEventId && (
              <Button
                size="sm"
                onClick={handleDelete}
                variant="destructive"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Loader2 className="size-4 animate-spin mr-2" />
                    Cancelling...
                  </>
                ) : (
                  <>
                    <Trash className="size-4 mr-2" />
                    Cancel Event
                  </>
                )}
              </Button>
            )}
            <Button
              size="sm"
              onClick={handleClose}
              variant="outline"
              disabled={loading}
            >
              Close
            </Button>
            <Button
              size="sm"
              onClick={handleSubmit}
              disabled={
                !meeting.subject ||
                !meetingTypeSelected ||
                loading ||
                !!authError
              }
            >
              {loading ? (
                <>
                  <Loader2 className="size-4 animate-spin mr-2" />
                  {editingEventId ? "Updating..." : "Creating..."}
                </>
              ) : editingEventId ? (
                "Update Event"
              ) : (
                "Create Event"
              )}
            </Button>
          </div>
        </div>
      </Dialog>
    );
  }
);

NewMeeting.displayName = "NewMeeting";
